import axiosInstance from './axios-instance';

export type AssistantChatSender = 'assistant' | 'user';

export interface AssistantChatSession {
  id: number;
  sessionId: string;
  title?: string | null;
  context?: any;
  createdAt?: string;
  updatedAt?: string;
}

export interface AssistantChatMessage {
  id: number;
  sessionId: string;
  sender: AssistantChatSender;
  text: string;
  metadata?: any;
  createdAt?: string;
}

export interface AssistantChatFeedback {
  id: number;
  sessionId: string;
  messageId?: number | null;
  accountId?: number;
  clientId?: number | null;
  userId?: number;
  user?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
  isHelpful?: boolean | null;
  isUseful?: boolean | null;
  reviewedByUserId?: number | null;
  reviewedAt?: string | null;
  reviewComment?: string | null;
  processedAt?: string | null;
  comment: string;
  metadata?: any;
  createdAt?: string;
  session?: AssistantChatSession;
  message?: AssistantChatMessage;
}

export interface ListSessionsResponse {
  sessions: AssistantChatSession[];
}

export interface ListMessagesResponse {
  messages: AssistantChatMessage[];
}

export interface CreateSessionRequest {
  sessionId?: string;
  title?: string | null;
  context?: any;
}

export interface CreateSessionResponse {
  session: AssistantChatSession;
}

export interface CreateMessageRequest {
  sessionId: string;
  sender: AssistantChatSender;
  text: string;
  metadata?: any;
  context?: any;
  title?: string | null;
}

export interface CreateMessageResponse {
  message: AssistantChatMessage;
  session?: AssistantChatSession;
}

export interface CreateFeedbackRequest {
  sessionId: string;
  messageId?: number;
  comment: string;
  isHelpful?: boolean | null;
  clientId?: number;
  metadata?: any;
}

export interface CreateFeedbackResponse {
  feedback: AssistantChatFeedback;
}

export interface ReviewFeedbackResponse {
  feedback: AssistantChatFeedback;
}

export interface ListFeedbackResponse {
  feedback: AssistantChatFeedback[];
}

export const listAssistantChatSessions = async (limit = 50): Promise<AssistantChatSession[]> => {
  const { data } = await axiosInstance.get<ListSessionsResponse>('/ai/chat/sessions', {
    params: { limit },
  });
  return data?.sessions || [];
};

export const createAssistantChatSession = async (
  payload: CreateSessionRequest
): Promise<AssistantChatSession> => {
  const { data } = await axiosInstance.post<CreateSessionResponse>('/ai/chat/session', payload);
  return data.session;
};

export const listAssistantChatMessages = async (
  sessionId: string
): Promise<AssistantChatMessage[]> => {
  const { data } = await axiosInstance.get<ListMessagesResponse>('/ai/chat/messages', {
    params: { sessionId },
  });
  return data?.messages || [];
};

export const listAssistantChatMessagesForAccount = async (
  sessionId: string
): Promise<AssistantChatMessage[]> => {
  const { data } = await axiosInstance.get<ListMessagesResponse>('/ai/chat/messages', {
    params: { sessionId, scope: 'account' },
  });
  return data?.messages || [];
};

export const createAssistantChatMessage = async (
  payload: CreateMessageRequest
): Promise<CreateMessageResponse> => {
  const { data } = await axiosInstance.post<CreateMessageResponse>('/ai/chat/message', payload);
  return data;
};

export const createAssistantChatFeedback = async (
  payload: CreateFeedbackRequest
): Promise<AssistantChatFeedback> => {
  const { data } = await axiosInstance.post<CreateFeedbackResponse>('/ai/chat/feedback', payload);
  return data.feedback;
};

export const listAssistantChatFeedback = async (
  sessionId: string,
  messageId?: number
): Promise<AssistantChatFeedback[]> => {
  const { data } = await axiosInstance.get<ListFeedbackResponse>('/ai/chat/feedback', {
    params: {
      sessionId,
      ...(messageId ? { messageId } : {}),
    },
  });
  return data?.feedback || [];
};

export const listAssistantChatFeedbackForAccount = async (
  sessionId: string
): Promise<AssistantChatFeedback[]> => {
  const { data } = await axiosInstance.get<ListFeedbackResponse>('/ai/chat/feedback', {
    params: {
      sessionId,
      scope: 'account',
    },
  });
  return data?.feedback || [];
};

export const listAssistantChatFeedbackAll = async (
  limit = 500
): Promise<AssistantChatFeedback[]> => {
  const { data } = await axiosInstance.get<ListFeedbackResponse>('/ai/chat/feedback', {
    params: { all: 1, allAccounts: 1, limit },
  });
  return data?.feedback || [];
};

export const reviewAssistantChatFeedback = async (
  feedbackId: number,
  approved: boolean,
  reviewComment?: string | null
): Promise<AssistantChatFeedback> => {
  const { data } = await axiosInstance.post<ReviewFeedbackResponse>('/ai/chat/feedback/review', {
    feedbackId,
    isApproved: approved,
    reviewComment,
  });
  return data.feedback;
};
